import { ANREDEN, GRUPPENARTEN, SAISONS } from "@/api/constants";
import { ALLOWED_TRANSITIONS } from "@/api/bookingDetail";
import type { Booking, BookingPatch } from "@/api/bookings";

export type AdminFieldType =
  | "text"
  | "number"
  | "decimal"
  | "date"
  | "time"
  | "select"
  | "checkbox"
  | "textarea"
  | "eur"
  | "units";

export type AdminSectionId =
  | "ids"
  | "kontakt"
  | "gruppenleiter"
  | "zeit"
  | "beleg"
  | "status"
  | "preis"
  | "rechnung"
  | "konus"
  | "bemerkung";

export type AdminSection = {
  id: AdminSectionId;
  label: string;
  hint?: string;
};

export type AdminFieldDef = {
  key: keyof Booking & string;
  patchKey?: keyof BookingPatch & string;
  label: string;
  type: AdminFieldType;
  section: AdminSectionId;
  options?: readonly string[];
  readOnly?: boolean;
  hint?: string;
  suffix?: string;
};

export const ADMIN_SECTIONS: AdminSection[] = [
  { id: "ids", label: "Identifikatoren", hint: "Referenz, Buchungsdatum" },
  { id: "kontakt", label: "Kontakt & Stammdaten", hint: "Anrede, Name, Adresse" },
  { id: "gruppenleiter", label: "Gruppenleiter", hint: "Optional, für Meldeschein" },
  { id: "zeit", label: "Zeitraum", hint: "Check-in/-out, Saison" },
  { id: "beleg", label: "Belegung", hint: "Personen, Einheiten, Schlüssel" },
  { id: "status", label: "Status & Workflow", hint: "Status-Flags & Versand-Datum" },
  { id: "preis", label: "Preise & Anzahlung" },
  { id: "rechnung", label: "Rechnung & Zahlung" },
  { id: "konus", label: "Konus", hint: "Meldeschein, Token" },
  { id: "bemerkung", label: "Bemerkung" },
];

const STATUS_OPTIONS = Object.keys(ALLOWED_TRANSITIONS);
const SAISON_OPTIONS = SAISONS;

export const ADMIN_FIELDS: readonly AdminFieldDef[] = [
  // Identifikatoren
  { key: "id", label: "Buchung-ID", type: "number", section: "ids", readOnly: true },
  { key: "buchungsreferenz", label: "Buchungsreferenz", type: "text", section: "ids", readOnly: true, hint: "System-generiert" },
  { key: "buchungsdatum", patchKey: "buchungsdatum", label: "Buchungsdatum", type: "date", section: "ids" },

  // Kontakt & Stammdaten
  { key: "anrede", patchKey: "anrede", label: "Anrede *", type: "select", section: "kontakt", options: ANREDEN },
  { key: "vorname", patchKey: "vorname", label: "Vorname *", type: "text", section: "kontakt" },
  { key: "nachname", patchKey: "nachname", label: "Nachname *", type: "text", section: "kontakt" },
  { key: "gruppenname", patchKey: "gruppenname", label: "Gruppenname *", type: "text", section: "kontakt" },
  { key: "gruppenart", patchKey: "gruppenart", label: "Gruppenart", type: "select", section: "kontakt", options: GRUPPENARTEN },
  { key: "gastname", patchKey: "gastname", label: "Gastname (intern)", type: "text", section: "kontakt", hint: "Legacy/abgeleitet" },
  { key: "email", patchKey: "email", label: "E-Mail", type: "text", section: "kontakt" },
  { key: "telefon", patchKey: "telefon", label: "Telefon", type: "text", section: "kontakt" },
  { key: "adresse_strasse", patchKey: "adresse_strasse", label: "Straße", type: "text", section: "kontakt" },
  { key: "adresse_plz", patchKey: "adresse_plz", label: "PLZ", type: "text", section: "kontakt" },
  { key: "adresse_ort", patchKey: "adresse_ort", label: "Ort", type: "text", section: "kontakt" },
  { key: "adresse_land", patchKey: "adresse_land", label: "Land", type: "text", section: "kontakt" },

  // Gruppenleiter
  { key: "gruppenleiter_anrede", patchKey: "gruppenleiter_anrede", label: "Anrede", type: "select", section: "gruppenleiter", options: ANREDEN },
  { key: "gruppenleiter_vorname", patchKey: "gruppenleiter_vorname", label: "Vorname", type: "text", section: "gruppenleiter" },
  { key: "gruppenleiter_nachname", patchKey: "gruppenleiter_nachname", label: "Nachname", type: "text", section: "gruppenleiter" },
  { key: "gruppenleiter_geburtsdatum", patchKey: "gruppenleiter_geburtsdatum", label: "Geburtsdatum", type: "date", section: "gruppenleiter" },
  { key: "gruppenleiter_adresse_strasse", patchKey: "gruppenleiter_adresse_strasse", label: "Straße", type: "text", section: "gruppenleiter" },
  { key: "gruppenleiter_adresse_hausnummer", patchKey: "gruppenleiter_adresse_hausnummer", label: "Hausnummer", type: "text", section: "gruppenleiter" },
  { key: "gruppenleiter_adresse_plz", patchKey: "gruppenleiter_adresse_plz", label: "PLZ", type: "text", section: "gruppenleiter" },
  { key: "gruppenleiter_adresse_ort", patchKey: "gruppenleiter_adresse_ort", label: "Ort", type: "text", section: "gruppenleiter" },
  { key: "gruppenleiter_adresse_land", patchKey: "gruppenleiter_adresse_land", label: "Land", type: "text", section: "gruppenleiter" },

  // Zeitraum
  { key: "check_in", patchKey: "check_in", label: "Check-in", type: "date", section: "zeit" },
  { key: "check_in_time", patchKey: "check_in_time", label: "Anreise-Uhrzeit", type: "time", section: "zeit" },
  { key: "check_out", patchKey: "check_out", label: "Check-out", type: "date", section: "zeit" },
  { key: "check_out_time", patchKey: "check_out_time", label: "Abreise-Uhrzeit", type: "time", section: "zeit" },
  { key: "saison", patchKey: "saison", label: "Saison", type: "select", section: "zeit", options: SAISON_OPTIONS },

  // Belegung
  { key: "einheiten_ids", patchKey: "einheiten_ids", label: "Einheiten", type: "units", section: "beleg" },
  { key: "personen_ab_16", patchKey: "personen_ab_16", label: "Personen ab 16", type: "number", section: "beleg" },
  { key: "kinder_6_bis_15", patchKey: "kinder_6_bis_15", label: "Kinder 6–15", type: "number", section: "beleg" },
  { key: "kinder_unter_6", patchKey: "kinder_unter_6", label: "Kinder unter 6", type: "number", section: "beleg" },
  { key: "gaesteanzahl", label: "Gäste gesamt", type: "number", section: "beleg", readOnly: true, hint: "Wird aus den drei Gruppen abgeleitet" },
  { key: "schluesselcode_eg", patchKey: "schluesselcode_eg", label: "Schlüsselcode EG", type: "text", section: "beleg" },
  { key: "schluesselcode_og", patchKey: "schluesselcode_og", label: "Schlüsselcode OG", type: "text", section: "beleg" },
  { key: "schluesselcode_dg", patchKey: "schluesselcode_dg", label: "Schlüsselcode DG", type: "text", section: "beleg" },
  { key: "schluesselcode_lz", patchKey: "schluesselcode_lz", label: "Schlüsselcode LZ", type: "text", section: "beleg" },

  // Status & Workflow
  { key: "status", patchKey: "status", label: "Status", type: "select", section: "status", options: STATUS_OPTIONS },
  { key: "bestaetigung_extern_versandt", patchKey: "bestaetigung_extern_versandt", label: "Bestätigung extern versandt", type: "checkbox", section: "status" },
  { key: "bestaetigung_extern_versandt_am", patchKey: "bestaetigung_extern_versandt_am", label: "Bestätigung versandt am", type: "date", section: "status" },
  { key: "infos_gruppe_extern_versandt", patchKey: "infos_gruppe_extern_versandt", label: "Infos an Gruppe versandt", type: "checkbox", section: "status" },
  { key: "infos_gruppe_extern_versandt_am", patchKey: "infos_gruppe_extern_versandt_am", label: "Infos versandt am", type: "date", section: "status" },
  { key: "storniert_am", patchKey: "storniert_am", label: "Storniert am", type: "date", section: "status" },

  // Preise & Anzahlung
  { key: "nachreinigung_eur", patchKey: "nachreinigung_eur", label: "Nachreinigung", type: "eur", section: "preis" },
  { key: "anzahlung_betrag", patchKey: "anzahlung_betrag", label: "Anzahlung-Betrag", type: "eur", section: "preis" },
  { key: "anzahlung_erhalten", patchKey: "anzahlung_erhalten", label: "Anzahlung erhalten", type: "checkbox", section: "preis" },
  { key: "anzahlung_erhalten_am", patchKey: "anzahlung_erhalten_am", label: "Anzahlung erhalten am", type: "date", section: "preis" },
  { key: "rechnung_rabatt_prozent", patchKey: "rechnung_rabatt_prozent", label: "Rabatt", type: "decimal", section: "preis", suffix: "%" },
  { key: "rechnung_rabatt_grund", patchKey: "rechnung_rabatt_grund", label: "Rabatt-Grund", type: "text", section: "preis" },

  // Rechnung & Zahlung
  { key: "kurtaxe_betrag", patchKey: "kurtaxe_betrag", label: "Kurtaxe-Betrag", type: "eur", section: "rechnung" },
  { key: "rechnungsbetrag_gesamt", patchKey: "rechnungsbetrag_gesamt", label: "Rechnungsbetrag gesamt", type: "eur", section: "rechnung" },
  { key: "rechnung_versandt_am", patchKey: "rechnung_versandt_am", label: "Rechnung versandt am", type: "date", section: "rechnung" },
  { key: "rechnung_zahlungsziel", patchKey: "rechnung_zahlungsziel", label: "Zahlungsziel", type: "text", section: "rechnung", hint: "z.B. '14 Tage netto'" },
  { key: "rechnung_bemerkung", patchKey: "rechnung_bemerkung", label: "Bemerkung auf Rechnung", type: "textarea", section: "rechnung" },
  { key: "rechnung_fehlteile", patchKey: "rechnung_fehlteile", label: "Fehlteile", type: "textarea", section: "rechnung" },
  { key: "bezahlt_am", patchKey: "bezahlt_am", label: "Bezahlt am", type: "date", section: "rechnung" },

  // Konus
  { key: "meldescheinnummer", patchKey: "meldescheinnummer", label: "Meldescheinnummer", type: "text", section: "konus" },
  { key: "konus_link_versandt", patchKey: "konus_link_versandt", label: "Konus-Link versandt", type: "checkbox", section: "konus" },
  { key: "konus_link_versandt_am", patchKey: "konus_link_versandt_am", label: "Konus-Link versandt am", type: "date", section: "konus" },
  { key: "konus_daten_eingetragen", patchKey: "konus_daten_eingetragen", label: "Konus-Daten eingetragen", type: "checkbox", section: "konus" },
  { key: "konus_daten_eingetragen_am", patchKey: "konus_daten_eingetragen_am", label: "Konus-Daten eingetragen am", type: "date", section: "konus" },

  // Bemerkung
  { key: "bemerkung", patchKey: "bemerkung", label: "Interne Bemerkung", type: "textarea", section: "bemerkung" },
];

export function fieldsForSection(section: AdminSectionId): AdminFieldDef[] {
  return ADMIN_FIELDS.filter((f) => f.section === section);
}

export function filterFields(query: string): AdminFieldDef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...ADMIN_FIELDS];
  return ADMIN_FIELDS.filter(
    (f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q),
  );
}
